/**
 * Check-In Service
 * Manages VA check-ins within shifts
 */

import { supabase } from '../utils/supabase';

// Constants
const CHECKIN_INTERVAL_MINUTES = 30;
const HOURS_PER_CHECKIN = 0.5;
const GRACE_PERIOD_MINUTES = 5; // Grace period after due time

/**
 * Create a new check-in
 * @param {Object} checkInData - Check-in data
 * @param {string} checkInData.shiftId - Shift ID
 * @param {string} checkInData.vaId - VA's user ID
 * @param {string} checkInData.photoUrl - URL of captured photo (signed URL)
 * @param {string} checkInData.photoStoragePath - Storage path of photo
 * @param {string} checkInData.taskDescription - Task description/remarks
 * @param {Array<string>} checkInData.tags - Optional tags
 * @param {Object} checkInData.metadata - Optional metadata (location, device info)
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const createCheckIn = async (checkInData) => {
  try {
    const { shiftId, vaId, photoUrl, photoStoragePath, taskDescription, metadata = {} } = checkInData;

    // Get the shift to verify it's active
    const { data: shift, error: shiftError } = await supabase
      .from('va_shifts')
      .select('*')
      .eq('id', shiftId)
      .eq('va_id', vaId)
      .eq('status', 'active')
      .single();

    if (shiftError || !shift) {
      return { 
        data: null, 
        error: new Error('No active shift found. Please start a shift first.') 
      };
    }

    const now = new Date();
    const dueTime = new Date(shift.next_checkin_due);
    
    // Calculate verification status based on timeliness
    // The verification_status column indicates whether the check-in was auto-approved or needs review
    // For now, all check-ins are auto-approved immediately (can be changed to 'pending_review' if needed)
    const verificationStatus = 'auto_approved';

    // Track if check-in was late (but don't use this as the verification_status)
    const diffMinutes = Math.floor((now - dueTime) / 60000);
    const isLate = diffMinutes > GRACE_PERIOD_MINUTES;

    // Create the check-in
    const { data: checkIn, error: insertError } = await supabase
      .from('va_checkins')
      .insert({
        shift_id: shiftId,
        va_id: vaId,
        photo_url: photoUrl,
        photo_storage_path: photoStoragePath,
        tasks_description: taskDescription,
        verification_status: verificationStatus,
        check_in_time: now.toISOString(),
        is_late: isLate,
        late_by_minutes: isLate ? diffMinutes : 0,
        device_info: metadata,
        contract_id: shift.contract_id,
        employer_id: shift.employer_id
      })
      .select()
      .single();

    if (insertError) throw insertError;

    // Update the shift with next check-in time and totals
    const nextCheckinDue = new Date(now.getTime() + CHECKIN_INTERVAL_MINUTES * 60000);
    
    const { error: updateError } = await supabase
      .from('va_shifts')
      .update({
        next_checkin_due: nextCheckinDue.toISOString(),
        last_checkin_at: now.toISOString(),
        total_hours: parseFloat(shift.total_hours || 0) + HOURS_PER_CHECKIN
      })
      .eq('id', shiftId);

    if (updateError) {
      console.error('Error updating shift:', updateError);
    }

    return { data: checkIn, error: null };
  } catch (error) {
    console.error('Error creating check-in:', error);
    return { data: null, error };
  }
};

/**
 * Get all check-ins for a shift
 * @param {string} shiftId - Shift ID
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export const getCheckInsForShift = async (shiftId) => {
  try {
    const { data: checkIns, error } = await supabase
      .from('va_checkins')
      .select('*')
      .eq('shift_id', shiftId)
      .order('check_in_time', { ascending: true });

    if (error) throw error;

    return { data: checkIns || [], error: null };
  } catch (error) {
    console.error('Error getting check-ins for shift:', error);
    return { data: null, error };
  }
};

/**
 * Get today's check-ins for a VA
 * @param {string} vaId - VA's user ID
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export const getTodayCheckIns = async (vaId) => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const { data: checkIns, error } = await supabase
      .from('va_checkins')
      .select(`
        *,
        shift:va_shifts (
          id,
          contract_id,
          status
        )
      `)
      .eq('va_id', vaId)
      .gte('check_in_time', today.toISOString())
      .lt('check_in_time', tomorrow.toISOString())
      .order('check_in_time', { ascending: true });

    if (error) throw error;

    return { data: checkIns || [], error: null };
  } catch (error) {
    console.error('Error getting today\'s check-ins:', error);
    return { data: null, error };
  }
};

/**
 * Get check-in history for a VA with date range
 * @param {string} vaId - VA's user ID
 * @param {Object} options - Query options
 * @param {Date} options.startDate - Start date
 * @param {Date} options.endDate - End date
 * @param {number} options.limit - Max results
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export const getCheckInHistory = async (vaId, options = {}) => {
  try {
    const { startDate, endDate, limit = 100 } = options;

    let query = supabase
      .from('va_checkins')
      .select(`
        *,
        shift:va_shifts (
          id,
          contract_id,
          start_time,
          end_time,
          status,
          contracts!contract_id (
            id,
            job_id,
            jobs!job_id (
              id,
              title
            )
          )
        )
      `)
      .eq('va_id', vaId)
      .order('check_in_time', { ascending: false })
      .limit(limit);

    if (startDate) {
      query = query.gte('check_in_time', new Date(startDate).toISOString());
    }

    if (endDate) {
      query = query.lte('check_in_time', new Date(endDate).toISOString());
    }

    const { data: checkIns, error } = await query;

    if (error) throw error;

    return { data: checkIns || [], error: null };
  } catch (error) {
    console.error('Error getting check-in history:', error);
    return { data: null, error };
  }
};

/**
 * Verify a check-in (employer action)
 * @param {string} checkInId - Check-in ID
 * @param {string} employerId - Employer's user ID (for verification)
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const verifyCheckIn = async (checkInId, employerId) => {
  try {
    // First verify the employer owns the contract
    const { data: checkIn, error: fetchError } = await supabase
      .from('va_checkins')
      .select(`
        *,
        shift:va_shifts (
          employer_id
        )
      `)
      .eq('id', checkInId)
      .single();

    if (fetchError || !checkIn) {
      return { data: null, error: new Error('Check-in not found') };
    }

    if (checkIn.shift?.employer_id !== employerId) {
      return { data: null, error: new Error('Not authorized to verify this check-in') };
    }

    const { data: updated, error: updateError } = await supabase
      .from('va_checkins')
      .update({
        is_verified: true,
        verified_at: new Date().toISOString(),
        verified_by: employerId
      })
      .eq('id', checkInId)
      .select()
      .single();

    if (updateError) throw updateError;

    return { data: updated, error: null };
  } catch (error) {
    console.error('Error verifying check-in:', error);
    return { data: null, error };
  }
};

/**
 * Flag a check-in (employer action)
 * @param {string} checkInId - Check-in ID
 * @param {string} employerId - Employer's user ID
 * @param {string} reason - Reason for flagging
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const flagCheckIn = async (checkInId, employerId, reason) => {
  try {
    // First verify the employer owns the contract
    const { data: checkIn, error: fetchError } = await supabase
      .from('va_checkins')
      .select(`
        *,
        shift:va_shifts (
          employer_id
        )
      `)
      .eq('id', checkInId)
      .single();

    if (fetchError || !checkIn) {
      return { data: null, error: new Error('Check-in not found') };
    }

    if (checkIn.shift?.employer_id !== employerId) {
      return { data: null, error: new Error('Not authorized to flag this check-in') };
    }

    const { data: updated, error: updateError } = await supabase
      .from('va_checkins')
      .update({
        is_flagged: true,
        flag_reason: reason
      })
      .eq('id', checkInId)
      .select()
      .single();

    if (updateError) throw updateError;

    return { data: updated, error: null };
  } catch (error) {
    console.error('Error flagging check-in:', error);
    return { data: null, error };
  }
};

/**
 * Get check-in statistics for a VA
 * @param {string} vaId - VA's user ID
 * @param {Object} options - Date range options
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const getCheckInStats = async (vaId, options = {}) => {
  try {
    const { startDate, endDate } = options;

    let query = supabase
      .from('va_checkins')
      .select('*')
      .eq('va_id', vaId);

    if (startDate) {
      query = query.gte('checkin_time', new Date(startDate).toISOString());
    }

    if (endDate) {
      query = query.lte('checkin_time', new Date(endDate).toISOString());
    }

    const { data: checkIns, error } = await query;

    if (error) throw error;

    const stats = {
      totalCheckIns: checkIns.length,
      onTimeCheckIns: checkIns.filter(c => c.status === 'on_time').length,
      lateCheckIns: checkIns.filter(c => c.status === 'late').length,
      verifiedCheckIns: checkIns.filter(c => c.is_verified).length,
      flaggedCheckIns: checkIns.filter(c => c.is_flagged).length,
      totalHoursLogged: checkIns.reduce((sum, c) => sum + (parseFloat(c.hours_credited) || 0), 0),
      onTimeRate: 0,
      verificationRate: 0
    };

    if (stats.totalCheckIns > 0) {
      stats.onTimeRate = (stats.onTimeCheckIns / stats.totalCheckIns) * 100;
      stats.verificationRate = (stats.verifiedCheckIns / stats.totalCheckIns) * 100;
    }

    return { data: stats, error: null };
  } catch (error) {
    console.error('Error getting check-in stats:', error);
    return { data: null, error };
  }
};

/**
 * Get check-ins for employer view (by contract or all)
 * @param {string} employerId - Employer's user ID
 * @param {Object} options - Query options
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export const getEmployerCheckIns = async (employerId, options = {}) => {
  try {
    const { contractId, startDate, endDate, limit = 50, unverifiedOnly = false } = options;

    let query = supabase
      .from('va_checkins')
      .select(`
        *,
        va:user_profiles!va_id (
          id,
          full_name,
          profile_picture
        ),
        shift:va_shifts!inner (
          id,
          employer_id,
          contract_id,
          contracts!contract_id (
            id,
            job_id,
            jobs!job_id (
              id,
              title
            )
          )
        )
      `)
      .eq('shift.employer_id', employerId)
      .order('check_in_time', { ascending: false })
      .limit(limit);

    if (contractId) {
      query = query.eq('shift.contract_id', contractId);
    }

    if (startDate) {
      query = query.gte('check_in_time', new Date(startDate).toISOString());
    }

    if (endDate) {
      query = query.lte('check_in_time', new Date(endDate).toISOString());
    }

    if (unverifiedOnly) {
      query = query.eq('is_verified', false);
    }

    const { data: checkIns, error } = await query;

    if (error) throw error;

    return { data: checkIns || [], error: null };
  } catch (error) {
    console.error('Error getting employer check-ins:', error);
    return { data: null, error };
  }
};

/**
 * Get check-ins for a timesheet entry with pagination
 * Queries by shift_id if available, otherwise by time range
 * @param {Object} params - Query parameters
 * @param {string} params.shiftId - Shift ID (preferred)
 * @param {string} params.vaId - VA user ID (required if no shiftId)
 * @param {string} params.contractId - Contract ID (required if no shiftId)
 * @param {string} params.startTime - Start time ISO string (required if no shiftId)
 * @param {string} params.endTime - End time ISO string (required if no shiftId)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 10)
 * @returns {Promise<{data: Array|null, error: Error|null, hasMore: boolean, total: number}>}
 */
export const getCheckInsForTimesheetEntry = async (params) => {
  try {
    const { 
      shiftId, 
      vaId, 
      contractId, 
      startTime, 
      endTime,
      page = 1, 
      limit = 10 
    } = params;

    // Calculate offset for pagination
    const offset = (page - 1) * limit;

    let query;
    let countQuery;

    if (shiftId) {
      // Query by shift_id (preferred method)
      query = supabase
        .from('va_checkins')
        .select('*')
        .eq('shift_id', shiftId)
        .order('check_in_time', { ascending: true })
        .range(offset, offset + limit - 1);

      countQuery = supabase
        .from('va_checkins')
        .select('id', { count: 'exact', head: true })
        .eq('shift_id', shiftId);
    } else if (vaId && contractId && startTime && endTime) {
      // Fallback: Query by time range and VA/contract
      query = supabase
        .from('va_checkins')
        .select('*')
        .eq('va_id', vaId)
        .eq('contract_id', contractId)
        .gte('check_in_time', startTime)
        .lte('check_in_time', endTime)
        .order('check_in_time', { ascending: true })
        .range(offset, offset + limit - 1);

      countQuery = supabase
        .from('va_checkins')
        .select('id', { count: 'exact', head: true })
        .eq('va_id', vaId)
        .eq('contract_id', contractId)
        .gte('check_in_time', startTime)
        .lte('check_in_time', endTime);
    } else {
      throw new Error('Either shiftId or (vaId, contractId, startTime, endTime) must be provided');
    }

    // Execute both queries
    const [{ data: checkIns, error }, { count, error: countError }] = await Promise.all([
      query,
      countQuery
    ]);

    if (error) throw error;
    if (countError) throw countError;

    const total = count || 0;
    const hasMore = offset + limit < total;

    return { 
      data: checkIns || [], 
      error: null, 
      hasMore,
      total,
      page,
      limit
    };
  } catch (error) {
    console.error('Error getting check-ins for timesheet entry:', error);
    return { data: null, error, hasMore: false, total: 0 };
  }
};

/**
 * Regenerate signed URL for a check-in photo
 * Useful when existing signed URLs have expired
 * @param {string} photoStoragePath - Storage path of the photo
 * @returns {Promise<{url: string|null, error: Error|null}>}
 */
export const regeneratePhotoUrl = async (photoStoragePath) => {
  try {
    if (!photoStoragePath) {
      throw new Error('Photo storage path is required');
    }

    const { data, error } = await supabase.storage
      .from('checkin-photos')
      .createSignedUrl(photoStoragePath, 60 * 60 * 24 * 7); // 7 days

    if (error) throw error;

    return { url: data.signedUrl, error: null };
  } catch (error) {
    console.error('Error regenerating photo URL:', error);
    return { url: null, error };
  }
};

/**
 * Subscribe to check-in updates for real-time changes
 * @param {string} shiftId - Shift ID to subscribe to
 * @param {Function} callback - Callback for updates
 * @returns {Object} Supabase subscription
 */
export const subscribeToCheckIns = (shiftId, callback) => {
  return supabase
    .channel(`checkins_${shiftId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'va_checkins',
        filter: `shift_id=eq.${shiftId}`
      },
      callback
    )
    .subscribe();
};

/**
 * Upload check-in photo to storage
 * @param {string} vaId - VA's user ID
 * @param {Blob} photoBlob - Photo blob from webcam
 * @returns {Promise<{url: string|null, storagePath: string|null, error: Error|null}>}
 */
export const uploadCheckInPhoto = async (vaId, photoBlob) => {
  try {
    // Validate inputs
    if (!vaId || !photoBlob) {
      throw new Error('Missing VA ID or photo blob');
    }

    if (!(photoBlob instanceof Blob)) {
      throw new Error('Photo must be a Blob object');
    }

    // Check if we have an active session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      console.error('Session check failed:', sessionError);
      throw new Error(`Auth session error: ${sessionError.message}`);
    }
    if (!session) {
      throw new Error('No active session - user must be logged in to upload photos');
    }

    console.log('Session verified for upload:', { userId: session.user.id, vaId });

    const timestamp = Date.now();
    const storagePath = `${vaId}/${timestamp}.jpg`;

    console.log('Uploading check-in photo:', { 
      storagePath, 
      blobSize: photoBlob.size, 
      blobType: photoBlob.type,
      userId: session.user.id
    });

    // Upload directly using supabase storage (accepts Blob)
    const { data, error } = await supabase.storage
      .from('checkin-photos')
      .upload(storagePath, photoBlob, {
        contentType: 'image/jpeg',
        upsert: false,
        cacheControl: '3600'
      });

    if (error) {
      console.error('Upload error details:', { 
        message: error.message, 
        status: error.status, 
        statusCode: error.statusCode,
        name: error.name 
      });
      throw new Error(`Storage upload failed: ${error.message}`);
    }

    console.log('Photo uploaded successfully:', data);

    // Generate signed URL (valid for 7 days)
    const { data: urlData, error: urlError } = await supabase.storage
      .from('checkin-photos')
      .createSignedUrl(storagePath, 60 * 60 * 24 * 7); // 7 days

    if (urlError) {
      console.error('Signed URL error:', urlError);
      throw new Error(`Failed to generate signed URL: ${urlError.message}`);
    }

    console.log('Signed URL generated successfully');
    return { url: urlData.signedUrl, storagePath, error: null };
  } catch (error) {
    console.error('Error uploading check-in photo:', error);
    return { url: null, storagePath: null, error };
  }
};

export default {
  createCheckIn,
  getCheckInsForShift,
  getTodayCheckIns,
  getCheckInHistory,
  getCheckInsForTimesheetEntry,
  regeneratePhotoUrl,
  verifyCheckIn,
  flagCheckIn,
  getCheckInStats,
  getEmployerCheckIns,
  subscribeToCheckIns,
  uploadCheckInPhoto,
  CHECKIN_INTERVAL_MINUTES,
  HOURS_PER_CHECKIN,
  GRACE_PERIOD_MINUTES
};
